import { Column, PsaViewSchema } from '@fsf/types';
import { _ } from '@ngx-translate/core';
import * as z from 'zod';

const PsaSchema = PsaViewSchema;

export type Psa = z.infer<typeof PsaSchema>;

const psaColumns: Column<keyof Psa>[] = [
  {
    header: 'psa.id',
    field: 'id',
    filterField: 'id',
    sortField: 'id',
    type: 'text',
    displayType: 'text',
    showOperator: false,
    showMatchModes: false,
    showAddButton: false,
  },
  {
    header: 'psa.type',
    field: 'type',
    filterField: 'type',
    sortField: 'type',
    type: 'text',
    textOptions: ['adult', 'child'],
    matchMode: 'in',
    showOperator: false,
    showMatchModes: false,
    showAddButton: false,
  },
  {
    header: 'psa.tags',
    field: 'tags',
    type: 'text',
    displayType: 'text',
    showOperator: false,
    showMatchModes: false,
    showAddButton: false,
  },
  {
    // No sortField/filterField: global search is the intended access path,
    // the harness and carabiner numbers live in the expanded detail row.
    header: 'psa.manufacturerNumberSet',
    field: 'manufacturer_number_set',
    type: 'text',
    displayType: 'text',
    showOperator: false,
    showMatchModes: false,
    showAddButton: false,
  },
  {
    header: 'psa.lastCheckedAt',
    field: 'last_checked_at',
    filterField: 'last_checked_at',
    sortField: 'last_checked_at',
    type: 'date',
    displayType: 'date',
    matchMode: 'between',
    showOperator: false,
    showMatchModes: true,
    showAddButton: false,
  },
];

const globalFilterFields = [
  'id',
  'type',
  'manufacturer_number_set',
  'manufacturer_number_harness',
  'manufacturer_number_carabiners',
];

/**
 * The two numbers that are only visible once a row is expanded. A search hit on
 * one of these is the case where the row alone shows no reason it matched.
 */
export const hiddenNumberFields = [
  'manufacturer_number_harness',
  'manufacturer_number_carabiners',
] as const;

export const PsaMetadata = {
  psaColumns,
  globalFilterFields,
};

const manufacturerNumber = z.string().max(255).nullable();

export const StorePsaSchema = z.object({
  type: z.enum(['adult', 'child']),
  manufacturer_number_set: manufacturerNumber,
  manufacturer_number_harness: manufacturerNumber,
  manufacturer_number_carabiners: manufacturerNumber,
});

/**
 * Defined standalone rather than as `StorePsaSchema.partial()`: the update
 * contract has no `type` at all, and the three numbers stay required-but-
 * nullable so clearing one is a value we send rather than a field we omit.
 */
export const UpdatePsaSchema = z.object({
  manufacturer_number_set: manufacturerNumber,
  manufacturer_number_harness: manufacturerNumber,
  manufacturer_number_carabiners: manufacturerNumber,
});

export type StorePsaPayload = z.infer<typeof StorePsaSchema>;
export type UpdatePsaPayload = z.infer<typeof UpdatePsaSchema>;

export const psaTranslations = _([
  'psa.add',
  'psa.id',
  'psa.type',
  'psa.typeAdult',
  'psa.typeChild',
  'psa.tags',
  'psa.lastCheckedAt',
  'psa.created',
  'psa.createError',
  'psa.deleteConfirmMessage',
  'psa.deleteHeader',
  'psa.reportDefect',
  'psa.defectHistory',
  'psa.openDefects',
  'psa.idAutoGenerated',
  'psa.edit',
  'psa.createHeader',
  'psa.editHeader',
  'psa.saved',
  'psa.saveError',
  'psa.manufacturerNumbers',
  'psa.manufacturerNumberSet',
  'psa.manufacturerNumberHarness',
  'psa.manufacturerNumberCarabiners',
  'psa.toggleDetails',
]);
